import { apiServerUrl, ExternalApi } from "@/apis/apiService";
import { useOrganisationStore } from "@/stores/organisationStore";
import { useOnboardingStore } from "@/stores/onboardingStore";
import {
    syncActiveOrganisationToLegacyStore,
    usePiniaStore,
} from "@/stores/store";
import { arePermissionObjectsEqual } from "@/methods/permissionsHelpers";
import { getRequestConfig } from "@esenda/core-ui/utils";
import { HTTP_METHOD } from "@esenda/core-ui/constants";
import {
    CustomerOrganisationProfile,
    OrganisationProfile,
    UserPermissions,
} from "@/types/school";

/** Deduplicates concurrent profile requests for the same authenticated user. */
let profileRequestInFlight:
    { email: string; request: Promise<void> } | undefined;

const isAuthenticatedAs = (email: string): boolean => {
    const pStore = usePiniaStore();
    return pStore.getIsAuthenticated && pStore.getUserEmail === email;
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
    typeof value === "object" && value !== null && !Array.isArray(value);

const normaliseCustomerProfile = (
    response: unknown,
): OrganisationProfile[] | null => {
    if (!isRecord(response) || !Array.isArray(response.organisations)) {
        return null;
    }

    const profiles = response.organisations;
    if (!profiles.every(isRecord)) return null;

    const customerProfiles = profiles as CustomerOrganisationProfile[];
    const shortcodes = customerProfiles.map(({ shortcode }) => shortcode);
    if (
        shortcodes.some(
            (shortcode) =>
                typeof shortcode !== "string" || shortcode.trim() === "",
        )
    ) {
        return null;
    }
    if (new Set(shortcodes).size !== shortcodes.length) return null;

    return customerProfiles.map((profile) => ({
        ...profile,
        permissions: (profile.permissions ?? {}) as UserPermissions,
    }));
};

const setNewOrganisationContext = (
    organisations: OrganisationProfile[],
    email: string | null = null,
): boolean => {
    if (
        organisations.length === 0 ||
        !organisations.every(
            ({ name, permissions }) => Boolean(name) && isRecord(permissions),
        )
    ) {
        return false;
    }

    const shortcodes = organisations.map(({ shortcode }) => shortcode);

    // The API response is authoritative; the saved shortcode is only a preference.
    const organisationStore = useOrganisationStore();
    const activeShortcode = organisationStore.getActiveOrganisationShortcode;
    const favouriteShortcode =
        organisationStore.loadFavouriteOrganisation(email);
    const preferredShortcode =
        organisationStore.getPreferredOrganisation(email);
    organisationStore.setAvailableOrganisations(organisations);
    const selectedShortcode =
        [activeShortcode, favouriteShortcode, preferredShortcode].find(
            (shortcode): shortcode is string =>
                !!shortcode && shortcodes.includes(shortcode),
        ) ?? shortcodes[0];
    if (selectedShortcode !== activeShortcode) {
        organisationStore.selectOrganisation(selectedShortcode);
    }
    syncActiveOrganisationToLegacyStore();
    organisationStore.setPreferredOrganisation(email, selectedShortcode);
    if (email) useOnboardingStore().initialiseOnboarding(email, organisations);
    return true;
};

/**
 * Fetches the authenticated user's organisation profiles and updates the active context.
 * @param {string} email The email of the authenticated user.
 * @returns {Promise<OrganisationProfile[] | null>} The profiles, or null if the context could not be set
 * or the authenticated user changed before the response arrived.
 */
export const getUserOrganisations = async (
    email: string,
): Promise<OrganisationProfile[] | null> => {
    const externalApi = new ExternalApi();
    const { data, error } = await externalApi.callExternalApi(
        getRequestConfig(`${apiServerUrl}/customers/profile`, HTTP_METHOD.GET),
        {
            userAction: "ignore",
            sentryTransactionName: "getUserOrganisations",
            disable404Alert: true,
        },
    );

    // The user may have signed out or switched account while the request was in flight.
    if (!isAuthenticatedAs(email)) return null;
    if (error || data == null) return null;

    const organisations = normaliseCustomerProfile(data);
    if (!organisations || !setNewOrganisationContext(organisations, email)) {
        useOrganisationStore().clearOrganisationContext();
        return null;
    }
    return organisations;
};

/**
 * Loads customer organisation profiles once per concurrent authenticated user request.
 * @param {string} email The email of the authenticated user.
 * @returns {Promise<void>} A promise that resolves once the organisation profiles have been loaded.
 */
export const loadUserOrganisations = async (email: string): Promise<void> => {
    if (profileRequestInFlight?.email === email) {
        await profileRequestInFlight.request;
        return;
    }

    const request = getUserOrganisations(email).then(() => undefined);
    profileRequestInFlight = { email, request };
    try {
        await request;
    } finally {
        if (profileRequestInFlight?.request === request) {
            profileRequestInFlight = undefined;
        }
    }
};

/**
 * Calls the profile endpoint to check the user's organisation context and permissions.
 * Identifies if the user's organisation context or permissions have changed.
 * Returns true if the organisation context or permissions have changed, false otherwise.
 * @param {string} email  The email of the authenticated user.
 * @returns {Promise<boolean>} True if the organisation context or permissions have changed, false otherwise.
 */
export const checkUserOrganisations = async (
    email: string,
): Promise<boolean> => {
    const organisationStore = useOrganisationStore();
    const currentPermissions = organisationStore.getPermissions ?? {};
    const activeShortcode = organisationStore.getActiveOrganisationShortcode;
    const externalApi = new ExternalApi();
    const { data, error } = await externalApi.callExternalApi(
        getRequestConfig(`${apiServerUrl}/customers/profile`, HTTP_METHOD.GET),
        {
            userAction: "ignore",
            sentryTransactionName: "checkUserOrganisations",
            disable404Alert: true,
        },
    );

    if (!isAuthenticatedAs(email)) return false;
    if (error || data == null) return false;

    const organisations = normaliseCustomerProfile(data);
    if (!organisations || !setNewOrganisationContext(organisations, email)) {
        organisationStore.clearOrganisationContext();
        return true;
    }

    const activeOrganisation = organisations.find(
        ({ shortcode }) => shortcode === activeShortcode,
    );
    if (!activeOrganisation) return true;

    return !arePermissionObjectsEqual(
        currentPermissions,
        activeOrganisation.permissions,
    );
};
